import {
  ObjectWorkspaceHarness,
  type ObjectWorkspaceWorkbenchContext,
} from '@heidi/agents';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';

import {
  ObjectWorkspaceProjectChatTab,
  type ObjectWorkspaceChatTab,
  type ObjectWorkspaceChatTabLifecycle,
} from './object-workspace-project-chat-tab';

interface ObjectWorkspaceChatHarnessViewProps {
  object: ObjectWorkspaceWorkbenchContext['object'];
  tabs: ObjectWorkspaceChatTab[];
  activeTabId: string;
  announcement?: { id: number; text: string };
  showRunOptions: boolean;
  sourceSessionId?: string;
  onOpenLastSession: (sessionId: string) => void;
  onTabSelect: (tabId: string) => void;
  onAddTab: () => void;
  onDismissTab: (tabId: string) => void;
  onBindProject: (tabId: string, projectId: string) => boolean;
  onLifecycleChange: (tabId: string, lifecycle: ObjectWorkspaceChatTabLifecycle) => void;
  onSubmissionError: (tabId: string) => void;
  onSubmissionStart: (tabId: string, prompt: string) => void;
}

export function ObjectWorkspaceChatHarnessView({
  object,
  tabs,
  activeTabId,
  announcement,
  showRunOptions,
  sourceSessionId,
  onOpenLastSession,
  onTabSelect,
  onAddTab,
  onDismissTab,
  onBindProject,
  onLifecycleChange,
  onSubmissionError,
  onSubmissionStart,
}: ObjectWorkspaceChatHarnessViewProps): JSX.Element {
  return (
    <>
      <ObjectWorkspaceHarness
        object={object}
        tabs={tabs.map(({ id, title, kind, isOpeningTurnInFlight, isTurnInFlight }) => ({
          id,
          title,
          kind,
          isThinking: isOpeningTurnInFlight || isTurnInFlight,
          isDismissible: kind !== 'heidi',
        }))}
        activeTabId={activeTabId}
        onTabSelect={onTabSelect}
        onAddTab={onAddTab}
        onDismissTab={onDismissTab}
        onOpenLastSession={
          sourceSessionId ? () => onOpenLastSession(sourceSessionId) : undefined
        }
        heidiTabIcon={<HeidiLogoMark className="size-3.5 shrink-0" />}
        renderTabContent={(descriptor, { isActive }) => {
          const tab = tabs.find((candidate) => candidate.id === descriptor.id);
          return tab ? (
            <ObjectWorkspaceProjectChatTab
              tab={tab}
              object={object}
              isActive={isActive}
              showRunOptions={showRunOptions}
              onBindProject={onBindProject}
              onLifecycleChange={onLifecycleChange}
              onSubmissionError={onSubmissionError}
              onSubmissionStart={onSubmissionStart}
            />
          ) : null;
        }}
      />
      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        data-testid="object-workspace-real-chat-announcer"
        data-announcement-id={announcement?.id ?? 0}
      >
        {announcement ? <span key={announcement.id}>{announcement.text}</span> : null}
      </div>
    </>
  );
}
